/*
 * THE PARTS OF THE HEBREW BIBLE THAT ARE NOT IN HEBREW.
 *
 * A corpus declares one `original.lang`, and for `HB` that is `he`. It is wrong for 263 passages:
 * Daniel 2:4–7:28, Ezra 4:8–6:18 and 7:12–26, and Jeremiah 10:11 are Aramaic. Found 2026-08-19 on
 * `c/daniel-2`, whose title read "Daniel 2 in Hebrew and English" over a chapter that is almost
 * entirely Aramaic, with every verse served as `lang="he"`.
 *
 * It matters twice. A screen reader given `lang="he"` applies Hebrew pronunciation to Aramaic —
 * the milder cousin of the fault `types.ts` records, where 57 passages of Hebrew sat inside a
 * document declared `lang="en"`. And the page TITLE is derived from the languages actually shown,
 * which is the mechanism this project uses to avoid overclaiming; it inherited a wrong answer
 * because the corpus record was the only place a language was stated.
 *
 * DECLARED, NOT DETECTED, and the reason is the cost of being wrong. Aramaic is detectable — the
 * determined ending on `מַלְכָּא`, the relative `דִּי` — but a classifier that misfires puts a false
 * claim about what language a scripture is written in onto a page. `CLAUDE.md` warns against a
 * retyped registry because a second copy goes stale beside the thing it describes; the boundaries
 * of the Aramaic sections have been fixed for two thousand years and this list cannot drift.
 *
 * WHAT VERSE-LEVEL TAGGING CANNOT SAY, stated rather than hidden:
 *
 *   Daniel 2:4    opens in Hebrew — "Then the Chaldeans spoke to the king in Aramaic:" — and the
 *                 Aramaic begins after the colon. It is declared Aramaic here because that is what
 *                 most of the verse is and what a reader meets, and no per-verse language can be
 *                 true of both halves.
 *   Genesis 31:47 holds two Aramaic words, `יְגַר שָׂהֲדוּתָא`, inside a Hebrew verse. It is NOT
 *                 declared, because two words do not make the verse Aramaic and claiming so would
 *                 be a worse error than the one being fixed.
 */

/** A run of passages in one language, as the books themselves are divided. */
interface Section {
  readonly book: string;
  readonly from: { readonly chapter: number; readonly verse: number };
  readonly to: { readonly chapter: number; readonly verse: number };
}

/**
 * The Aramaic of the Hebrew Bible, in reading order.
 *
 * Boundaries are the standard ones and are not this project's judgement: they are where the text
 * changes language, agreed across every edition and every tradition that reads these books.
 */
export const ARAMAIC_SECTIONS: readonly Section[] = [
  { book: 'Daniel', from: { chapter: 2, verse: 4 }, to: { chapter: 7, verse: 28 } },
  { book: 'Ezra', from: { chapter: 4, verse: 8 }, to: { chapter: 6, verse: 18 } },
  { book: 'Ezra', from: { chapter: 7, verse: 12 }, to: { chapter: 7, verse: 26 } },
  { book: 'Jeremiah', from: { chapter: 10, verse: 11 }, to: { chapter: 10, verse: 11 } },
];

/** Chapter and verse as one comparable number, so a range test is one comparison per end. */
function at(chapter: number, verse: number): number {
  return chapter * 1_000 + verse;
}

/**
 * The language a passage's original is actually in, where it is not the corpus's own.
 *
 * `undefined` means "the corpus record is right", which is the answer for every passage of every
 * other text. Only `HB` has a section in another language today; nothing here assumes that stays
 * true, and a corpus that never appears in the sections simply never matches.
 */
export function originalLangOf(
  corpus: string,
  book: string,
  chapter: number,
  verse: number,
): string | undefined {
  if (corpus !== 'HB') return undefined;
  const here = at(chapter, verse);
  for (const section of ARAMAIC_SECTIONS) {
    if (section.book !== book) continue;
    if (here < at(section.from.chapter, section.from.verse)) continue;
    if (here > at(section.to.chapter, section.to.verse)) continue;
    return 'arc';
  }
  return undefined;
}

/**
 * The same answer from a reference, because that is what a page holds.
 *
 * A page has "Daniel 2:5" and not three fields, and every caller parsing that for itself would be
 * the same rule written in as many places as there are page families. A reference that does not
 * split into a chapter and a verse — a whole chapter, a hadith category — yields nothing, which is
 * the honest answer: no per-verse language can be true of a range.
 */
export function originalLangOfRef(corpus: string, ref: string): string | undefined {
  const m = /^(.+?)\s+(\d+):(\d+)\s*$/.exec(String(ref ?? ''));
  if (!m) return undefined;
  return originalLangOf(corpus, m[1], Number(m[2]), Number(m[3]));
}
